import { PageContext, PageType, ContextualSuggestion } from '@/types/pageContext';

class PageAnalysisService {
  generateContextualSuggestions(pageContext: PageContext): ContextualSuggestion[] {
    const suggestions: ContextualSuggestion[] = [];
    
    // Add type-specific suggestions
    suggestions.push(...this.getPageTypeSuggestions(pageContext.pageType));
    
    // Add content-based suggestions
    suggestions.push(...this.getContentBasedSuggestions(pageContext));
    
    // Add feature-based suggestions
    suggestions.push(...this.getFeatureBasedSuggestions(pageContext.features));
    
    // Sort by priority and return top 4
    return suggestions
      .sort((a, b) => b.priority - a.priority)
      .slice(0, 4);
  }

  private getPageTypeSuggestions(pageType: PageType): ContextualSuggestion[] {
    const suggestions: Record<PageType, ContextualSuggestion[]> = {
      dashboard: [
        {
          id: 'dashboard-metrics',
          text: 'Como interpretar estas métricas?',
          type: 'question',
          priority: 9,
          pageType: 'dashboard'
        },
        {
          id: 'dashboard-overview',
          text: 'Explique o painel de controle',
          type: 'question',
          priority: 8,
          pageType: 'dashboard'
        }
      ],
      chat: [
        {
          id: 'chat-optimize',
          text: 'Como otimizar minhas perguntas?',
          type: 'question',
          priority: 9,
          pageType: 'chat'
        },
        {
          id: 'chat-features',
          text: 'Quais recursos de chat posso usar?',
          type: 'question',
          priority: 8,
          pageType: 'chat'
        }
      ],
      documents: [
        {
          id: 'doc-review',
          text: 'Revisar este documento',
          type: 'action',
          priority: 10,
          pageType: 'documents'
        },
        {
          id: 'doc-format',
          text: 'Como formatar adequadamente?',
          type: 'question',
          priority: 9,
          pageType: 'documents'
        },
        {
          id: 'doc-templates',
          text: 'Sugerir modelo de documento',
          type: 'action',
          priority: 8,
          pageType: 'documents'
        }
      ],
      calculators: [
        {
          id: 'calc-explain',
          text: 'Explicar este cálculo',
          type: 'question',
          priority: 10,
          pageType: 'calculators'
        },
        {
          id: 'calc-validate',
          text: 'Verificar dados inseridos',
          type: 'action',
          priority: 9,
          pageType: 'calculators'
        }
      ],
      settings: [
        {
          id: 'settings-guide',
          text: 'Guia das configurações',
          type: 'question',
          priority: 8,
          pageType: 'settings'
        },
        {
          id: 'settings-optimize',
          text: 'Configurações recomendadas',
          type: 'action',
          priority: 7,
          pageType: 'settings'
        }
      ],
      plans: [
        {
          id: 'plans-compare',
          text: 'Comparar planos disponíveis',
          type: 'question',
          priority: 9,
          pageType: 'plans'
        },
        {
          id: 'plans-recommend',
          text: 'Qual plano é melhor para mim?',
          type: 'question',
          priority: 8,
          pageType: 'plans'
        }
      ],
      legal: [
        {
          id: 'legal-explain',
          text: 'Explicar termos legais',
          type: 'question',
          priority: 8,
          pageType: 'legal'
        }
      ],
      notifications: [
        {
          id: 'notifications-manage',
          text: 'Como gerenciar notificações?',
          type: 'question',
          priority: 7,
          pageType: 'notifications'
        }
      ],
      tutorials: [
        {
          id: 'tutorials-start',
          text: 'Por onde começar?',
          type: 'question',
          priority: 8,
          pageType: 'tutorials'
        }
      ],
      admin: [
        {
          id: 'admin-help',
          text: 'Ajuda com administração',
          type: 'question',
          priority: 7,
          pageType: 'admin'
        }
      ],
      home: [
        {
          id: 'home-info',
          text: 'Explore funcionalidades da plataforma',
          type: 'action',
          priority: 8,
          pageType: 'home'
        }
      ],
      unknown: []
    };

    return suggestions[pageType] || [];
  }

  private getContentBasedSuggestions(pageContext: PageContext): ContextualSuggestion[] {
    const suggestions: ContextualSuggestion[] = [];
    const content = pageContext.content.toLowerCase();

    // Check for specific content patterns
    if (content.includes('erro') || content.includes('error')) {
      suggestions.push({
        id: 'help-error',
        text: 'Ajuda com este erro',
        type: 'action',
        priority: 10,
        pageType: pageContext.pageType
      });
    }

    if (content.includes('formulário') || content.includes('form')) {
      suggestions.push({
        id: 'help-form',
        text: 'Como preencher este formulário?',
        type: 'question',
        priority: 9,
        pageType: pageContext.pageType
      });
    }

    if (content.includes('tabela') || content.includes('dados')) {
      suggestions.push({
        id: 'help-data',
        text: 'Explicar dados da tabela',
        type: 'question',
        priority: 7,
        pageType: pageContext.pageType
      });
    }

    return suggestions;
  }

  private getFeatureBasedSuggestions(features: string[]): ContextualSuggestion[] {
    const suggestions: ContextualSuggestion[] = [];

    if (features.includes('Upload de Arquivos')) {
      suggestions.push({
        id: 'help-upload',
        text: 'Tipos de arquivo aceitos',
        type: 'question',
        priority: 8,
        pageType: 'unknown'
      });
    }

    if (features.includes('Editor de Texto')) {
      suggestions.push({
        id: 'help-editor',
        text: 'Recursos do editor',
        type: 'question',
        priority: 7,
        pageType: 'unknown'
      });
    }

    if (features.includes('Gráficos e Visualizações')) {
      suggestions.push({
        id: 'help-charts',
        text: 'Interpretar gráficos',
        type: 'question',
        priority: 6,
        pageType: 'unknown'
      });
    }

    return suggestions;
  }

  getPageDescription(pageContext: PageContext): string {
    const descriptions: Record<PageType, string> = {
      dashboard: 'Painel principal com métricas e estatísticas do seu uso da plataforma',
      chat: 'Assistente de IA para conversas e suporte jurídico',
      documents: 'Editor e gerenciador de documentos jurídicos',
      calculators: 'Calculadoras jurídicas especializadas para diferentes áreas do direito',
      settings: 'Configurações da conta e personalização da plataforma',
      plans: 'Planos de assinatura e informações de cobrança',
      legal: 'Termos legais, política de privacidade e informações jurídicas',
      notifications: 'Central de notificações e alertas',
      tutorials: 'Tutoriais e guias de uso da plataforma',
      admin: 'Painel administrativo (acesso restrito)',
      home: 'Página inicial da plataforma Justiçai',
      unknown: 'Página da plataforma'
    };

    return descriptions[pageContext.pageType] || 'Página atual da plataforma';
  }
}

export const pageAnalysisService = new PageAnalysisService();